import { chatWithAdminToken, chatWithMerchantToken, chatWithoutToken } from '@lib/api/v2';

export interface ChatRoomFilter {
  status?: 'open' | 'closed' | 'all';
  merchantId?: string;
  keyword?: string;
  limit?: number;
}

export interface ChatSettingPayload {
  merchantId?: string;
  slug: string;
  isEnabled: boolean;
  welcomeMessage?: string;
  aiEnabled: boolean;
  aiMode: 'suggest' | 'auto_read' | 'auto_action';
  autoDoorOpenEnabled: boolean;
  ownerAlertPhone?: string;
  branchGuide?: string;
}

const authedChatApi = (isAdmin: boolean) => (isAdmin ? chatWithAdminToken : chatWithMerchantToken);

export const getPublicChatSetting = (slug: string) =>
  chatWithoutToken.get(`/public/settings/${slug}`);

export const createPublicChatRoom = (data: {
  slug: string;
  customerName?: string;
  customerPhone?: string;
  message: string;
}) => chatWithoutToken.post('/public/rooms', data);

export const getPublicChatRoom = (roomUid: string, customerToken: string) =>
  chatWithoutToken.get(`/public/rooms/${roomUid}`, { params: { customerToken } });

export const sendPublicChatMessage = (
  roomUid: string,
  data: { customerToken: string; message: string },
) => chatWithoutToken.post(`/public/rooms/${roomUid}/messages`, data);

export const readPublicChatRoom = (roomUid: string, customerToken: string) =>
  chatWithoutToken.patch(`/public/rooms/${roomUid}/read`, { customerToken });

export const requestPublicChatVerification = (
  roomUid: string,
  data: { customerToken: string; phoneNum: string },
) => chatWithoutToken.post(`/public/rooms/${roomUid}/verification`, data);

export const confirmPublicChatVerification = (
  roomUid: string,
  data: { customerToken: string; phoneNum: string; code: string },
) => chatWithoutToken.post(`/public/rooms/${roomUid}/verification/confirm`, data);

export const requestPublicDoorOpen = (roomUid: string, customerToken: string) =>
  chatWithoutToken.post(`/public/rooms/${roomUid}/actions/door-open`, { customerToken });

export const getChatSetting = (isAdmin: boolean, merchantId?: string) =>
  authedChatApi(isAdmin).get('/settings', { params: { merchantId } });

export const saveChatSetting = (isAdmin: boolean, data: ChatSettingPayload) =>
  authedChatApi(isAdmin).put('/settings', data);

export const getChatRooms = (isAdmin: boolean, filter: ChatRoomFilter) =>
  authedChatApi(isAdmin).get('/rooms', {
    params: {
      ...filter,
      status: filter.status === 'all' ? undefined : filter.status,
    },
  });

export const getChatRoomDetail = (isAdmin: boolean, roomUid: string, merchantId?: string) =>
  authedChatApi(isAdmin).get(`/rooms/${roomUid}`, { params: { merchantId } });

export const sendStaffChatMessage = (
  isAdmin: boolean,
  roomUid: string,
  data: { message: string; merchantId?: string },
) => authedChatApi(isAdmin).post(`/rooms/${roomUid}/messages`, data);

export const updateChatRoomStatus = (
  isAdmin: boolean,
  roomUid: string,
  data: { status: 'open' | 'closed'; merchantId?: string },
) => authedChatApi(isAdmin).patch(`/rooms/${roomUid}/status`, data);

export const updateChatHandlingMode = (
  isAdmin: boolean,
  roomUid: string,
  data: { handlingMode: 'ai' | 'waiting_staff' | 'staff' | 'closed'; merchantId?: string },
) => authedChatApi(isAdmin).patch(`/rooms/${roomUid}/handling-mode`, data);

export const readStaffChatRoom = (
  isAdmin: boolean,
  roomUid: string,
  data?: { merchantId?: string },
) => authedChatApi(isAdmin).patch(`/rooms/${roomUid}/read`, data || {});
